import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { BusinessRegistrationForm } from '@/types/businessRegistration';
import { motion } from 'framer-motion';
import { 
  CheckCircle, ChevronDown, ChevronUp, Loader2, 
  Camera, School, Leaf, MapPin, Fish, Compass, Bird, Mountain, ArrowRight, Calendar, Clock, Users, CreditCard, Info, Globe, Phone, AlertCircle, FileText, ArrowLeft
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { getExperienceCategoriesByType, EXPERIENCE_CATEGORIES } from '@/constants/experienceCategories';

interface ExperienceStepProps {
  formData: BusinessRegistrationForm;
  setFormData: (data: BusinessRegistrationForm) => void;
  onBack: () => void;
  onSubmit?: () => void;
  onNext?: () => void;
  isSubmitting?: boolean;
  errors?: { [key: string]: string };
}

const bookingModes = [
  { 
    value: 'platform', 
    label: 'Platform Booking', 
    description: 'Use our platform for bookings and payments',
    icon: CreditCard,
    features: [
      'Integrated payment processing',
      'Automated booking management',
      'Customer reviews and ratings',
      'Marketing and visibility'
    ]
  },
  { 
    value: 'redirect', 
    label: 'External Booking', 
    description: 'Redirect customers to your website or WhatsApp',
    icon: Globe,
    features: [
      'Direct customer communication',
      'Full control over booking process',
      'Custom payment methods',
      'Flexible scheduling'
    ]
  }
];

export function ExperienceStep({ 
  formData, 
  setFormData, 
  onBack, 
  onSubmit,
  onNext,
  isSubmitting = false,
  errors = {}
}: ExperienceStepProps) {
  const [expandedDetail, setExpandedDetail] = useState<string | null>(null);
  const [selectedExperiences, setSelectedExperiences] = useState<string[]>(
    formData.businessData.experiences.map(exp => exp.id)
  );
  const [additionalInfo, setAdditionalInfo] = useState<string>(
    formData.businessData.additionalInfo || ''
  );
  const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // When the available experience categories change (due to activity type selection),
    // clear any local errors as they might be related to the previous set of options.
    setLocalErrors({});
  }, [formData.businessData.hasAnimalActivities, formData.businessData.hasAdventureActivities]);

  // Filter experience categories based on selected activity types using shared function
  const filteredExperienceCategories = getExperienceCategoriesByType(
    formData.businessData.hasAnimalActivities,
    formData.businessData.hasAdventureActivities
  );

  // Handle experience selection
  const toggleExperience = (experienceId: string) => {
    const newSelectedExperiences = selectedExperiences.includes(experienceId)
      ? selectedExperiences.filter(id => id !== experienceId)
      : [...selectedExperiences, experienceId];

    setSelectedExperiences(newSelectedExperiences);

    // Update the form data using the shared EXPERIENCE_CATEGORIES
    const updatedExperiences = newSelectedExperiences
      .map(id => EXPERIENCE_CATEGORIES.find(cat => cat.id === id))
      .filter(Boolean)
      .map(cat => ({
        id: cat!.id,
        type: cat!.type as 'animal' | 'adventure',
        name: cat!.name,
        description: cat!.description
      }));

    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        experiences: updatedExperiences
      }
    });
  };

  // Update additional info
  const handleAdditionalInfoChange = (value: string) => {
    setAdditionalInfo(value);
    setFormData({
      ...formData,
      businessData: {
        ...formData.businessData,
        additionalInfo: value
      }
    });
  };

  // Toggle expanded detail
  const toggleDetail = (id: string) => {
    setExpandedDetail(prev => prev === id ? null : id);
  };

  // Render helper for error messages
  const renderError = (field: string) => {
    const error = errors[field] || localErrors[field];
    if (!error) return null;
    return (
      <span className="text-sm text-red-500 mt-1">
        {error}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="space-y-6"
    >
      {/* Header Section with Clear Purpose */}
      <div className="text-center space-y-3 pb-6 border-b border-gray-200">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-[#97BC62] rounded-full flex items-center justify-center">
          <span className="text-2xl">{formData.businessData.emoji}</span>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Experience Categories</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
          Select the types of wildlife experiences you offer. This helps visitors find your business when searching for specific activities.
        </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Activity Type Summary */}
        <div className="flex gap-2 justify-center">
          {formData.businessData.hasAnimalActivities && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
              🦁 Animal Activities
            </span>
          )}
          {formData.businessData.hasAdventureActivities && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              🏔️ Adventure Activities
            </span>
          )}
        </div>
        
        {/* Experience Categories Selection */}
        <div className="space-y-3">
          {filteredExperienceCategories.map(category => {
            const Icon = category.icon;
            const isSelected = selectedExperiences.includes(category.id);
            
            return (
              <motion.div 
                key={category.id}
                className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                  isSelected ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-200 hover:border-gray-300'
                }`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.01 }}
              >
                <button
                  type="button"
                  onClick={() => toggleExperience(category.id)}
                  className="w-full p-4 text-left transition-all hover:bg-gray-50/50"
                  disabled={isSubmitting}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg transition-colors ${
                        isSelected ? 'bg-primary text-white' : 'bg-gray-100'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{category.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                      </div>
                  </div>
                  <div className="flex items-center gap-2">
                      {isSelected && (
                        <CheckCircle className="h-5 w-5 text-primary" />
                      )}
                      <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                          toggleDetail(category.id);
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                        aria-label={`${expandedDetail === category.id ? 'Hide' : 'Show'} details for ${category.name}`}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${
                          expandedDetail === category.id ? 'rotate-180' : ''
                        }`} />
                      </button>
                    </div>
                  </div>
                </button>
                
                {expandedDetail === category.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-gray-200 p-4 bg-gray-50/50"
                  >
                    <p className="text-sm text-gray-600 leading-relaxed">{category.description}</p>
                    {category.image && (
                      <div className="mt-3 text-xs text-gray-500">
                        <Info className="h-3 w-3 inline mr-1" />
                        This category includes specialized photo opportunities
                  </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
        
        {filteredExperienceCategories.length === 0 && (
          <motion.div 
            className="text-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Info className="h-8 w-8 text-yellow-600 mx-auto mb-3" />
            <h4 className="font-medium text-yellow-800 mb-2">No Experience Categories Available</h4>
            <p className="text-sm text-yellow-700">
              Please go back and select at least one activity type (Animal Activities or Adventure Activities) to see available experience categories.
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onBack}
              className="mt-3 border-yellow-300 text-yellow-700 hover:bg-yellow-100"
            >
              ← Go Back
            </Button>
          </motion.div>
        )}
        
        {errors.experiences && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {errors.experiences}
            </p>
          </motion.div>
        )}

        {/* Additional Information Section */}
        <div className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <h4 className="font-semibold text-gray-900">Additional Information</h4>
              </div>
                    <div className="space-y-2">
            <Label htmlFor="additionalInfo" className="text-sm font-medium text-gray-700">
              Tell us more about your business <span className="text-gray-400 font-normal">(Optional)</span>
                      </Label>
            <Textarea
              id="additionalInfo"
              value={additionalInfo}
              onChange={(e) => handleAdditionalInfoChange(e.target.value)}
              className="min-h-[100px] border-gray-300 focus:border-primary focus:ring-primary/20"
              placeholder="Any additional information about your business, special requirements, certifications, unique features, etc."
                        disabled={isSubmitting}
                      />
            <p className="text-xs text-gray-500">
              Share any special features, certifications, or unique aspects of your wildlife business
            </p>
          </div>
      </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button 
          type="button"
          variant="outline" 
          onClick={onBack}
          disabled={isSubmitting}
          className="min-w-[120px] transition-all duration-200 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        
        <Button 
          onClick={onNext || onSubmit}
          disabled={isSubmitting || selectedExperiences.length === 0}
          className="min-w-[120px] bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 transition-all duration-200"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Continue
              <ArrowRight className="h-4 w-4" />
            </span>
          )}
        </Button>
      </div>
    </motion.div>
  );
}